
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserAccess } from "@/hooks/useUserAccess";
import PinProtectedRoute from "@/components/PinProtectedRoute";

// Module-level flag: once auth+access have resolved once, never show spinner again
let hasEverResolved = false;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, isLoading: authLoading, signOut } = useAuth();
  const { hasAccess, isTrialExpired, tier, isFreeGift, isLoading: accessLoading } = useUserAccess();
  const navigate = useNavigate();
  const location = useLocation();

  // Track first resolution
  const [showSpinner, setShowSpinner] = useState(!hasEverResolved);

  useEffect(() => {
    if (!authLoading && !accessLoading) {
      hasEverResolved = true;
      setShowSpinner(false);
    }
  }, [authLoading, accessLoading]);

  useEffect(() => {
    // First check auth
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    
    // Don't redirect auth page for trial expiration - users need to be able to login/register
    if (location.pathname === '/auth') {
      return;
    }
    
    // Wait for tier to be loaded before making access decisions
    if (!accessLoading && tier === null) {
      return; // Still loading tier information
    }
    
    // Users with access tiers or active free-gift should never be redirected to trial expired
    if (['super_admin', 'internal', 'invited', 'subscribed'].includes(tier) || (isFreeGift && !isTrialExpired)) {
      return;
    }
    
    // If trial is expired, sign out the user and redirect to trial-expired
    if (!authLoading && !accessLoading && user && (isTrialExpired || !hasAccess)) {
      signOut();
      navigate('/trial-expired');
      return;
    }
  }, [user, authLoading, accessLoading, isTrialExpired, hasAccess, tier, navigate, location.pathname, signOut]);

  // Only show spinner on the very first auth check, never again
  if (showSpinner && (authLoading || accessLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Don't render anything if user is not authenticated
  // The useEffect will handle redirects
  if (!user) {
    return null;
  }

  // Wrap children with PIN protection
  return <PinProtectedRoute>{children}</PinProtectedRoute>;
};

export default ProtectedRoute;
